import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

const ASSIGNABLE_ROLES = ["CAJERO", "ENCARGADO", "DUEÑO"];

export default function SecurityAdminPanel({ store, access }) {
  const [emailInput, setEmailInput] = useState("");
  const [feedback, setFeedback] = useState(null);
  const canManageSecurity = access?.isOwner || access?.role === "ADMIN";

  const policy = store.securityPolicy || {
    ownerEmail: "",
    allowedEmails: [],
    roleByEmail: {},
  };

  const allowedRows = useMemo(() => {
    const ownerEmail = String(policy.ownerEmail || "").trim().toLowerCase();
    const allowed = Array.isArray(policy.allowedEmails) ? policy.allowedEmails : [];
    const unique = [...new Set(allowed.map((e) => String(e || "").trim().toLowerCase()).filter(Boolean))];

    return unique
      .filter((email) => email !== ownerEmail)
      .map((email) => ({
        email,
        role: policy.roleByEmail?.[email] || "CAJERO",
      }))
      .sort((a, b) => a.email.localeCompare(b.email));
  }, [policy.allowedEmails, policy.ownerEmail, policy.roleByEmail]);

  const handleAddEmail = async () => {
    const result = await store.agregarCorreoPermitido(emailInput);
    if (!result?.ok) {
      setFeedback({ tipo: "error", msg: result?.reason || "No se pudo agregar el correo" });
      return;
    }
    setFeedback({ tipo: "success", msg: "Correo permitido agregado" });
    setEmailInput("");
  };

  const handleRemove = async (email) => {
    const result = await store.removerCorreoPermitido(email);
    if (!result?.ok) {
      setFeedback({ tipo: "error", msg: result?.reason || "No se pudo remover" });
      return;
    }
    setFeedback({ tipo: "success", msg: "Correo removido" });
  };

  const handleRoleChange = async (email, role) => {
    const result = await store.asignarRolCorreo({ email, rol: role });
    if (!result?.ok) {
      setFeedback({ tipo: "error", msg: result?.reason || "No se pudo asignar rol" });
      return;
    }
    setFeedback({ tipo: "success", msg: `Rol actualizado para ${email}` });
  };

  if (!canManageSecurity) {
    return (
      <Alert severity="warning">
        Solo usuarios con rol ADMIN o DUEÑO pueden administrar seguridad.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} mb={2}>
        Administración de Seguridad
      </Typography>

      {feedback && (
        <Alert severity={feedback.tipo} sx={{ mb: 2 }}>
          {feedback.msg}
        </Alert>
      )}

      <Stack spacing={2} mb={2}>
        <Card variant="outlined">
          <CardContent>
            <Typography fontWeight={700} mb={1}>Propietario de la tienda</Typography>
            <Chip label={policy.ownerEmail || "Sin propietario"} color="primary" variant="outlined" />
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              El propietario siempre tiene acceso total. Nadie más puede ser ADMIN.
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography fontWeight={700} mb={1}>Correos permitidos (usuarios autenticados)</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                size="small"
                fullWidth
                label="Correo devssoft"
                placeholder="usuario@devssoft.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
              />
              <Button variant="contained" onClick={() => void handleAddEmail()}>
                Agregar
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Card variant="outlined">
        <CardContent>
          <Typography fontWeight={700} mb={1}>Usuarios habilitados</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700 } }}>
                  <TableCell>Correo</TableCell>
                  <TableCell>Rol</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allowedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No hay correos permitidos adicionales
                    </TableCell>
                  </TableRow>
                ) : (
                  allowedRows.map((row) => (
                    <TableRow key={row.email} hover>
                      <TableCell>{row.email}</TableCell>
                      <TableCell width={180}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={row.role}
                          onChange={(e) => {
                            void handleRoleChange(row.email, e.target.value);
                          }}
                        >
                          {ASSIGNABLE_ROLES.map((role) => (
                            <MenuItem key={role} value={role}>
                              {role}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell align="center" width={100}>
                        <IconButton color="error" size="small" onClick={() => void handleRemove(row.email)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}
